import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Stack,
    Link,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/auth/AuthContext';
import LoadingNavBar from '../../components/navbar/LoadingNavBar';
import Logo from '../../resources/images/logo/bizismart.png';
import InitialSignupForm from './SignupFlow/forms/InitialSignupForm';
import { handleSignup } from '../../components/signup/SignupModule';
import { StringKeys } from '../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import { UserStatus } from '../../components/routes/routing_statuses/RoutingStatuses';

export default function SignupPage() {
  const { user, createUser, loginUser } = useAuthContext();
  const [error, setError] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user !== UserStatus.UNAUTHENTICATED && user !== UserStatus.UNINITIALIZED) {
      navigate('/home', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (data) => {
    try {
      setError(null);
            
      const result = await handleSignup({ 
        ...data, 
        step: 'account',
        user,
        createUser,
        loginUser
      });
            
      if (result.success) {
        setTimeout(() => {
          navigate('/signup-completion', { replace: true });
        }, 500);
      } else {
        setError("Failed to create account. Please try again.");
      }
    } catch (error) {
      console.error("[SignupPage] Error in signup process:", error);
      setError(error.message);
    }
  };

  return (
    <>
      <LoadingNavBar showLoginButton={true} />
      <LoadingNavBar showLoginButton={false} />
        <Box
            className="hero-section"
            sx={{
                height: '100%',
                background: 'linear-gradient(135deg, rgba(57, 172, 231, 0.95), rgba(16, 42, 67, 0.95))',
                animation: 'gradientAnimation 15s ease infinite',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                '@keyframes gradientAnimation': {
                    '0%': { backgroundPosition: '0% 50%' },
                    '50%': { backgroundPosition: '100% 50%' },
                    '100%': { backgroundPosition: '0% 50%' },
                },
            }}
        >
            <Box
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                    borderRadius: 3,
                    textAlign: 'center',
                    width: '95%',
                    maxWidth: '440px',
                    margin: '0 auto',
                    my: 5,
                    p: { xs: 3, sm: 4 },
                    WebkitTransform: 'translateZ(0)',
                }}
            >
            <img src={Logo} alt="BiziSmart Logo" style={{ height: '60px', marginBottom: '20px' }} />
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold', color: '#102a43', mb: 3 }}>
              {t(StringKeys.CREATE_YOUR_ACCOUNT)}
            </Typography>
            
            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}

            <Stack spacing={3}>
              <InitialSignupForm onSubmit={handleSubmit} />
              
              <Typography variant="caption" sx={{ mt: 2, textAlign: 'center', color: '#64748b' }}>
                {t(StringKeys.BY_SIGNING_UP_YOU_AGREE_TO_OUR_)}{' '}
                <Link
                  onClick={() => navigate('/terms-of-service')}
                  underline="hover"
                  sx={{ cursor: 'pointer', color: '#39ace7', fontWeight: 'bold' }}
                >
                  {t(StringKeys.TERMS_OF_SERVICE)}
                </Link>{' '}
                {t(StringKeys.AND)}{' '}
                <Link
                  onClick={() => navigate('/privacy-policy')}
                  underline="hover"
                  sx={{ cursor: 'pointer', color: '#39ace7', fontWeight: 'bold' }}
                >
                  {t(StringKeys.PRIVACY_POLICY)}
                </Link>.
              </Typography>

              <Typography 
                variant="body2" 
                sx={{ 
                    textAlign: 'center',
                    color: '#64748b',
                    fontSize: '15px'
                }}
              >
                {t(StringKeys.ALREADY_HAVE_AN_ACCOUNT)}{' '}
                <Link
                    component="button"
                    onClick={() => navigate('/login')}
                    sx={{ 
                        cursor: 'pointer', 
                        color: '#39ace7', 
                        fontWeight: '600',
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline'
                        }
                    }}
                >
                    {t(StringKeys.LOG_IN)}
                </Link>
            </Typography>
            </Stack>
          </Box>
      </Box>
    </>
  );
} 